import React, { useEffect, useState } from 'react'
import { Redirect } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { t } from 'i18next'
import Navigation from 'Components/Navigation'
import useValidatePermission from 'Hooks/useValidatePermission'
import { VAPermissions, ROUTES } from 'Utils/constants'
import { fetchCompanies } from 'redux/actions/companyAction'
import {
  getPaymentMethods,
  getPaymentMethodFields,
  getPublicKey,
} from 'api/paymentConfig/paymentConfig.services'
import { importPublicKey } from 'Utils/paymentConfigEncryption'
import YappyCompanyCard from './components/YappyCompanyCard'
import {
  createAbortableRequest,
  createMountedGuard,
  isAbortError,
} from './utils/request.utils'
import './yappyConfig.css'

const YAPPY_CODE = '0017'

const YAPPY_COMPANY_ORDER = ['COCHEZ', 'NOVEY', 'PUNTO COCHEZ']
const YAPPY_COMPANY_ALIASES = {
  COCHEZ: ['COCHEZ', 'CO'],
  NOVEY: ['NOVEY', 'NO'],
  'PUNTO COCHEZ': ['PUNTO COCHEZ', 'PUNTOCOCHEZ', 'PC'],
}

function normalizeCompanyValue(value) {
  return `${value || ''}`
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
}

function getCompanyCode(company) {
  return normalizeCompanyValue(company.Code || company.CompanyCode)
}

function getCompanyName(company) {
  return (
    company.Name || company.CompanyName || company.Code || company.CompanyCode
  )
}

function getCompanyAlias(company) {
  const code = getCompanyCode(company)
  const name = normalizeCompanyValue(getCompanyName(company))

  return YAPPY_COMPANY_ORDER.find(
    companyName =>
      YAPPY_COMPANY_ALIASES[companyName].includes(code) ||
      YAPPY_COMPANY_ALIASES[companyName].includes(name)
  )
}

function getCompanyValue(company, alias) {
  return normalizeCompanyValue(alias || getCompanyName(company))
    .toLowerCase()
    .replace(/\s+/g, '_')
}

function buildYappyCompanies(companies = []) {
  return companies
    .filter(company => company && company.IsVisibleInPortalVAD !== false)
    .map(company => {
      const alias = getCompanyAlias(company)

      if (!alias || !company.CompanyId) return null

      return {
        label: getCompanyName(company) || alias,
        value: getCompanyValue(company, alias),
        companyId: company.CompanyId,
        order: YAPPY_COMPANY_ORDER.indexOf(alias),
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.order - b.order)
    .map(({ order, ...company }) => company)
}

function setCompanyError(index, key) {
  return prev =>
    prev.map((c, i) =>
      i === index ? { ...c, isLoading: false, error: t(key) } : c
    )
}

async function loadCompanyYappy(
  company,
  index,
  setCompaniesData,
  requestConfig,
  isMounted
) {
  try {
    const { data: methodsData } = await getPaymentMethods(
      company.companyId,
      requestConfig
    )

    if (!isMounted()) return

    const yappy = methodsData.Data?.find(m => m.Code === YAPPY_CODE)

    if (!yappy || !yappy.PaymentTypeId) {
      setCompaniesData(
        setCompanyError(index, 'SETTINGS_T.ERROR_NO_PAYMENT_METHOD')
      )
      return
    }

    const { data: fieldsData } = await getPaymentMethodFields(
      yappy.PaymentTypeId,
      requestConfig
    )

    if (!isMounted()) return

    setCompaniesData(prev =>
      prev.map((c, i) =>
        i === index
          ? {
              ...c,
              paymentTypeId: yappy.PaymentTypeId,
              fields: fieldsData.Data || [],
              isLoading: false,
            }
          : c
      )
    )
  } catch (error) {
    if (isAbortError(error) || !isMounted()) return

    setCompaniesData(setCompanyError(index, 'SETTINGS_T.ERROR_LOADING'))
  }
}

function getPublicKeyFromResponse(data) {
  return data?.Data?.publicKey ?? null
}

async function loadPublicKey(
  setPublicKey,
  setPublicKeyError,
  requestConfig,
  isMounted
) {
  try {
    const { data } = await getPublicKey(requestConfig)

    if (!isMounted()) return

    const rawPublicKey = getPublicKeyFromResponse(data)

    if (!rawPublicKey) {
      throw new Error('Public key not found')
    }

    const importedKey = await importPublicKey(rawPublicKey)

    if (!isMounted()) return

    setPublicKey(importedKey)
    setPublicKeyError(null)
  } catch (error) {
    if (isAbortError(error) || !isMounted()) return

    setPublicKey(null)
    setPublicKeyError(
      t('SETTINGS_T.ERROR_PUBLIC_KEY', {
        defaultValue: 'No fue posible cargar la llave publica de cifrado.',
      })
    )
  }
}

export default function YappyConfig() {
  const dispatch = useDispatch()
  const hasSettingsPermission = useValidatePermission(VAPermissions.settingsVA)

  const [companies, setCompanies] = useState([])
  const [companiesLoaded, setCompaniesLoaded] = useState(false)
  const [companiesData, setCompaniesData] = useState([])
  const [publicKey, setPublicKey] = useState(null)
  const [publicKeyError, setPublicKeyError] = useState(null)

  useEffect(() => {
    if (!hasSettingsPermission) return undefined

    const { isMounted, cleanup } = createMountedGuard()

    dispatch(fetchCompanies())
      .then(data => {
        if (!isMounted()) return

        setCompanies(Array.isArray(data) ? data : [])
      })
      .catch(() => {
        if (!isMounted()) return

        setCompanies([])
      })
      .finally(() => {
        if (!isMounted()) return

        setCompaniesLoaded(true)
      })

    return cleanup
  }, [dispatch, hasSettingsPermission])

  useEffect(() => {
    if (!hasSettingsPermission || !companiesLoaded) return undefined

    const yappyCompanies = buildYappyCompanies(companies)

    setCompaniesData(
      yappyCompanies.map(c => ({
        ...c,
        paymentTypeId: null,
        fields: [],
        isLoading: true,
        error: null,
      }))
    )

    if (!yappyCompanies.length) {
      return undefined
    }

    const {
      requestConfig,
      isMounted: isComponentMounted,
      abort,
    } = createAbortableRequest()

    yappyCompanies.forEach((company, index) =>
      loadCompanyYappy(
        company,
        index,
        setCompaniesData,
        requestConfig,
        isComponentMounted
      )
    )
    loadPublicKey(
      setPublicKey,
      setPublicKeyError,
      requestConfig,
      isComponentMounted
    )

    return abort
  }, [companies, companiesLoaded, hasSettingsPermission])

  if (!hasSettingsPermission) {
    return <Redirect to={ROUTES.home} />
  }

  return (
    <>
      <Navigation />
      <div
        className="yappy-config-container"
        data-testid="container-yappy-config">
        <h1 className="yappy-config-title">{t('SETTINGS_T.YAPPY')}</h1>
        <div className="yappy-config-grid">
          {companiesData.map(company => (
            <YappyCompanyCard
              key={company.value}
              company={company}
              publicKey={publicKey}
              publicKeyError={publicKeyError}
            />
          ))}
        </div>
      </div>
    </>
  )
}
